import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, type Variants } from 'framer-motion'
import { growMotion } from '@/src/renderer/lib/motion/growMotion'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { ShortcutBadge } from '@/src/renderer/components/ui/Button/ShortcutBadge'
import { CanvasMemoIcon, CanvasNoteIcon } from '../ProjectCanvas/icons'
import { ListToolbarAddIcon, ListToolbarSectionIcon } from './icons'
import type { ListToolbarProps, ListToolbarVariant } from './ListToolbar.types'
import { SWAP, SWAP_LEAVE_END, selectionComes, swapTimeline, withPlacement, type SwapKind } from './utils/swapTimeline'
import './ListToolbar.scss'

/** Abstand der Tooltips zum Knopf: die Polsterung der Pille (8px) und Luft dahinter. */
const TOOLTIP_OFFSET = 16

/*
  **Was beim Wechsel der Gestalt dazukommt, wächst Knopf für Knopf von seinem
  Platz aus** — wie die Marker der aufgeklappten Aufgabe (`TaskActionBar`,
  `growMotion` mit ihren Vorgaben), nur gestaffelt und halb so schnell. Gewählt im
  Prototyp `tools/list-toolbar-motion-prototype`, Variante D bei ½× (01.10.2026).

  Der Platz entsteht für alle zugleich, die Knöpfe erscheinen darin nacheinander
  von innen nach außen: Abschnitt, Notiz (mit Trenner), Memo. Beim Verschwinden
  rückwärts. Was bleibt, steht still; die Pille wächst mit, weil sie ihre Fläche
  trägt und die Segmente nicht.
*/
type Slot = 'section' | 'note' | 'memo' | 'selection'
const SLOT_ORDER: readonly Slot[] = ['section', 'note', 'memo', 'selection']
/** Stufe zwischen zwei Knöpfen (vor `PACE`). */
const STEP = 0.05
/** Halb so schnell wie die Marker (`growMotion`, `pace`). */
const PACE = 2

/** Wo ein Knopf in der laufenden Folge steht — für AnimatePresence (`custom`). */
interface Sequence {
  enter: Partial<Record<Slot, number>>
  exit: Partial<Record<Slot, number>>
  /** Notiz/Memo ↔ Auswahl tauschen gerade, oder die Auswahl kommt/geht allein (#291) — dann bewegt der Tausch, nicht `growMotion`. */
  swap?: boolean
}

const segmentMotion = (slot: Slot, sequence?: Sequence) =>
  growMotion({ axis: 'width', size: 'auto', pace: PACE, sequence: { enter: sequence?.enter[slot], exit: sequence?.exit[slot] } })

/*
  **Im Tausch** (#291) steht ein Segment sofort ganz da — die Knöpfe darin und die Breite des Platzes
  bewegt der Tausch selbst (`swapTimeline`). Was geht, bleibt stehen, bis der Platz fertig und das Letzte
  ausgeblendet ist (`SWAP_LEAVE_END`): Es liegt dann unsichtbar unter der neuen Ebene, erst danach darf es aus dem DOM.
*/
const SWAP_STEADY = { width: 'auto', opacity: 1, scale: 1, transition: { duration: 0 } }
const SWAP_HOLD = { opacity: 0, transition: { duration: 0.01, delay: SWAP_LEAVE_END } }

const variantsFor = (slot: Slot): Variants => ({
  hidden: (sequence?: Sequence) => (sequence?.swap ? SWAP_STEADY : segmentMotion(slot, sequence).initial),
  shown: (sequence?: Sequence) => (sequence?.swap ? SWAP_STEADY : segmentMotion(slot, sequence).animate),
  gone: (sequence?: Sequence) => (sequence?.swap ? SWAP_HOLD : segmentMotion(slot, sequence).exit),
})

const SEGMENT_VARIANTS: Record<Slot, Variants> = {
  section: variantsFor('section'),
  note: variantsFor('note'),
  memo: variantsFor('memo'),
  selection: variantsFor('selection'),
}

const SEGMENT_STYLE = segmentMotion('section').style

/**
 * Ein Segment, das mit der Gestalt kommt und geht. `initial` greift nur, wenn
 * die umgebende `AnimatePresence` es zulässt — beim ersten Bild steht es da.
 * `sequence` sagt, wann es in der Folge dran ist; beim Gehen liefert sie die
 * `AnimatePresence` (dort gilt ihr `custom`).
 */
const Segment: React.FC<{ slot: Slot; sequence: Sequence; className?: string; children: React.ReactNode }> = ({ slot, sequence, className, children }) => (
  <motion.div
    className={`list-toolbar__segment${className ? ` ${className}` : ''}`}
    variants={SEGMENT_VARIANTS[slot]}
    custom={sequence}
    initial="hidden"
    animate="shown"
    exit="gone"
    style={SEGMENT_STYLE}
  >
    {children}
  </motion.div>
)

/** Die Beschriftung bewegt sich im Takt der Breadcrumb, im selben Tempo wie die Knöpfe. */
const labelMotion = growMotion({ axis: 'width', size: 'auto', content: 0.2, enterDelay: 0.05, exitDelay: 0.1, pace: PACE })

/**
 * Die Beschriftung von „Neue Aufgabe" — die einzige, die beim Wechsel der
 * Gestalt kommt und geht (`short` ↔ `listOnlyShort`/`compact`/`justAdd`); die
 * übrigen stehen nur im leeren Zustand und dort fest. Der Abstand zum Icon
 * sitzt IN ihr (`margin-left`), nicht als `gap` am Knopf: Sonst spränge er beim
 * Kommen und Gehen um 8px.
 */
const Label: React.FC<{ text: string }> = ({ text }) => (
  <motion.span
    className="list-toolbar__label-shell"
    initial={labelMotion.initial}
    animate={labelMotion.animate}
    exit={labelMotion.exit}
    style={labelMotion.style}
  >
    <span className="list-toolbar__label">{text}</span>
  </motion.span>
)

/**
 * Die Folge für diesen Wechsel: gezählt wird nur unter dem, was sich gerade
 * ändert — beim Pinnwand-Wechsel ist die Notiz die erste Stufe, am Rand der
 * Abschnitt.
 */
export function sequenceFor(before: Partial<Record<Slot, boolean>>, after: Partial<Record<Slot, boolean>>): Sequence {
  const entering = SLOT_ORDER.filter(slot => after[slot] && !before[slot])
  const leaving = SLOT_ORDER.filter(slot => before[slot] && !after[slot])
  return {
    enter: Object.fromEntries(entering.map((slot, i) => [slot, i * STEP])),
    // Rückwärts: was zuletzt kam, geht zuerst.
    exit: Object.fromEntries(leaving.map((slot, i) => [slot, (leaving.length - 1 - i) * STEP])),
  }
}

const VARIANT_CLASS: Record<ListToolbarVariant, string> = {
  emptyState: 'empty-state',
  short: 'short',
  compact: 'compact',
  listOnlyLong: 'list-only-long',
  listOnlyShort: 'list-only-short',
  addOnly: 'add-only',
  justAdd: 'just-add',
}

/**
 * Die **Listen-Werkzeug-Pille** (Figma `List Toolbar`, 5550:75776) — legt eine
 * Aufgabe, einen Abschnitt, eine Notiz oder ein Memo an. Schwebt unten in der
 * Aufgabenliste und steht auf der leeren Projektseite (CONTEXT.md).
 *
 * Nur die Gestalt: Wohin angelegt wird, wann sie welche Gestalt hat und wo sie
 * steht, entscheidet der Aufrufer (`ListToolbarDock`, `CanvasEmptyStateTeaser`).
 *
 * Der **Griff** ist wie bei der Pinnwand-Pille ein reines Mauswerkzeug — kein
 * Knopf, kein Tab-Halt.
 *
 * @example
 * <ListToolbar variant="short" onAddTask={…} onAddSection={…} onPlaceNote={…} onPlaceMemo={…} onGripPointerDown={drag.gripPointerDown} />
 * @example
 * <ListToolbar variant="emptyState" onAddTask={…} onAddSection={…} onPlaceNote={…} onPlaceMemo={…} />
 */
export const ListToolbar = React.forwardRef<HTMLDivElement, ListToolbarProps>(function ListToolbar({
  variant, onAddTask, onAddSection, onPlaceNote, onPlaceMemo, onNotePointerDown, onMemoPointerDown,
  onAddTaskPointerDown, addTaskDragging = false, onAddSectionPointerDown, addSectionDragging = false,
  onGripPointerDown, gripPressed = false, disabled = false, selection, taskLabel = true, className,
}, ref) {
  const { t } = useTranslation()

  // Bei Auswahl (#291) räumen Notiz und Memo den Platz für ihre Befehle; der Text an „Neue Aufgabe“
  // bleibt, solange Platz ist (`taskLabel`, Sven 08.10.2026). Am Rand eingeklappt (`justAdd`) bleibt die
  // Auswahl draußen; der Dock fährt die Pille dafür aus.
  const showSelection = Boolean(selection) && variant !== 'justAdd'
  const labelTask = taskLabel && (variant === 'emptyState' || variant === 'short' || variant === 'listOnlyLong' || variant === 'addOnly')
  const labelOthers = variant === 'emptyState'
  const showSection = variant !== 'justAdd' && variant !== 'addOnly'
  // Notiz und Memo nur mit Ziel — ohne Pinnwand (Listen-Modus) fehlen sie.
  const placementVariant = variant === 'emptyState' || variant === 'short' || variant === 'compact'
  const showNote = placementVariant && Boolean(onPlaceNote) && !showSelection
  const showMemo = placementVariant && Boolean(onPlaceMemo) && !showSelection
  const showGrip = variant !== 'emptyState' && Boolean(onGripPointerDown)

  // Was beim letzten Bild stand — im Effekt gemerkt, damit StrictModes doppeltes
  // Rendern denselben Vorher-Stand sieht.
  const visible: Record<Slot, boolean> = { section: showSection, note: showNote, memo: showMemo, selection: showSelection }
  const previousRef = useRef(visible)
  useEffect(() => {
    previousRef.current = visible
  })

  /*
    **Der Tausch** (#291, Variante C): Gehen Notiz und Memo, weil die Auswahl kommt — oder umgekehrt —,
    teilen sich beide EINEN Platz hinter dem Trenner. Er bleibt gesetzt, solange der Tausch läuft, über
    alle Bilder dazwischen; Pinnwand auf/zu und das Ausfahren am Rand laufen weiter über `growMotion`.
    **Ohne Notiz und Memo** (Kurz notiert, Pinnwand offen) kommt und geht die Auswahl genauso, mit ihrem
    Trenner (Sven, 08.10.2026) — nur nicht, wenn zugleich der Abschnitt kommt oder geht: Das ist das
    Ausfahren am Rand.
  */
  const placementShown = showNote || showMemo
  const [slots, setSlots] = useState({ placement: placementShown, selection: showSelection })
  const [swap, setSwap] = useState<SwapKind | null>(null)
  if (slots.placement !== placementShown || slots.selection !== showSelection) {
    setSlots({ placement: placementShown, selection: showSelection })
    const alone = !slots.placement && !placementShown && previousRef.current.section === showSection
    setSwap(
      slots.placement && !slots.selection && showSelection && !placementShown ? 'toSelection'
        : slots.selection && !slots.placement && placementShown && !showSelection ? 'toPlacement'
          : alone && showSelection ? 'selectionIn'
            : alone && slots.selection ? 'selectionOut'
              : null,
    )
  }
  const sequence: Sequence = { ...sequenceFor(previousRef.current, visible), swap: swap !== null }

  const swapRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const wrapper = swapRef.current
    if (!swap || !wrapper) return
    // Die Auswahl liegt im Tausch über dem Platz (`--swapping`); der Platz misst so die Notiz/Memo-Breite —
    // ohne Notiz und Memo 0.
    const overlay = wrapper.querySelector<HTMLElement>(':scope > .list-toolbar__segment--selection')
    const placementWidth = wrapper.offsetWidth
    const selectionWidth = overlay?.offsetWidth ?? 0
    // Im versteckten Tab und in jsdom gibt es keine Maße: dann ohne Bewegung, nur das Aufräumen.
    const measured = selectionWidth > 0 && (placementWidth > 0 || !withPlacement(swap))
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    const k = measured && !reduced ? 1000 : 0
    const easing = `cubic-bezier(${SWAP.ease.join(', ')})`
    // Die Breite gehört dem Tausch und geht mit ihm; die Knöpfe laufen über sein Ende hinaus zu Ende.
    let width: Animation | null = null
    let dividerMotion: Animation | null = null
    const items: Animation[] = []

    if (measured && typeof wrapper.animate === 'function') {
      const [from, to] = selectionComes(swap) ? [placementWidth, selectionWidth] : [selectionWidth, placementWidth]
      width = wrapper.animate([{ width: `${from}px` }, { width: `${to}px` }], { duration: SWAP.duration * k, easing, fill: 'both' })
      // Allein kommt und geht der Trenner mit — im selben Zug, sonst spränge die Pille um seine 3px.
      const divider = wrapper.previousElementSibling
      if (!withPlacement(swap) && divider instanceof HTMLElement && divider.classList.contains('list-toolbar__segment--divider')) {
        const shown = { width: `${divider.offsetWidth}px`, opacity: 1 }
        const hidden = { width: '0px', opacity: 0 }
        dividerMotion = divider.animate(selectionComes(swap) ? [hidden, shown] : [shown, hidden], { duration: SWAP.duration * k, easing, fill: 'both' })
      }
      const selectionItems = [...(overlay?.querySelectorAll<HTMLElement>('[data-swap-item="selection"]') ?? [])]
      for (const step of swapTimeline(swap, selectionItems.length)) {
        const el = typeof step.item === 'number'
          ? selectionItems[step.item]
          : wrapper.querySelector<HTMLElement>(`[data-swap-item="${step.item}"]`)
        if (!el) continue
        const frame = ({ opacity, scale }: { opacity: number; scale: number }) => ({ opacity, transform: `scale(${scale})` })
        const animation = el.animate([frame(step.from), frame(step.to)], { duration: step.duration * k, delay: step.delay * k, easing, fill: 'both' })
        // Was bleibt, gibt seinen Stil am Ende zurück; was geht, verschwindet ohnehin mit seinem Segment.
        animation.finished.then(() => { if (step.to.opacity === 1) animation.cancel() }, () => {})
        items.push(animation)
      }
    }
    // Erst nach dem Gehen (`SWAP_HOLD`) wird die Auswahl wieder Teil der Reihe.
    let finished = false
    const done = setTimeout(() => { finished = true; setSwap(null) }, SWAP_LEAVE_END * 1000 + 80)
    return () => {
      clearTimeout(done)
      width?.cancel()
      dividerMotion?.cancel()
      // Abgebrochen (neuer Wechsel, Aushängen): auch die Knöpfe sofort auf ihren Stand.
      if (!finished) for (const animation of items) animation.cancel()
    }
  }, [swap])

  const buttonClass = (labelled: boolean, draggable = false) =>
    `list-toolbar__button${labelled ? ' list-toolbar__button--labelled' : ''}${draggable ? ' list-toolbar__button--draggable' : ''}`

  /*
    „Neue Aufgabe" nennt nicht EIN Kürzel, sondern die drei Wege, die es per
    Taste gibt (Figma 5556:179481): A oben, B unten, F über dem ersten
    Abschnitt. Der Knopf selbst legt nach eigenen Regeln an — der Tooltip
    zeigt, wie es gezielt geht. „Abschnitt" spiegelt das mit Umschalt:
    ⇧A darüber, ⇧B darunter (das tut der Knopf), ⇧F ganz oben.
  */
  const keysTooltip = (shift: boolean, entries: ReadonlyArray<readonly [string, string]>) => (
    <span className="list-toolbar__tooltip-keys">
      {entries.map(([key, label]) => (
        <span key={key} className="list-toolbar__tooltip-key">
          <ShortcutBadge shortcut={{ kind: 'character', text: key, shift }} variant="tooltip" />
          <span>{t(`listToolbar.${label}`)}</span>
        </span>
      ))}
    </span>
  )
  const addTaskTooltip = keysTooltip(false, [['A', 'tooltipTop'], ['B', 'tooltipBottom'], ['F', 'tooltipAboveFirst']])
  const addSectionTooltip = keysTooltip(true, [['A', 'tooltipSectionAbove'], ['B', 'tooltipSectionBelow'], ['F', 'tooltipSectionTop']])

  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label={t('listToolbar.label')}
      className={`list-toolbar list-toolbar--${VARIANT_CLASS[variant]}${className ? ` ${className}` : ''}`}
    >
      <div className="list-toolbar__group list-toolbar__group--add">
        {showGrip && (
          <WithTooltip side="top" sideOffset={TOOLTIP_OFFSET} content={gripPressed ? undefined : t('listToolbar.moveToolbar')}>
            <div
              className={`list-toolbar__grip${gripPressed ? ' list-toolbar__grip--pressed' : ''}`}
              onPointerDown={onGripPointerDown}
              draggable={false}
            />
          </WithTooltip>
        )}
        <WithTooltip side="top" sideOffset={TOOLTIP_OFFSET} content={addTaskDragging ? undefined : addTaskTooltip}>
          <button
            type="button"
            aria-label={t('listToolbar.addTask')}
            className={`${buttonClass(labelTask, Boolean(onAddTaskPointerDown))}${addTaskDragging ? ' list-toolbar__button--dragging' : ''}`}
            onClick={onAddTask}
            // Ein Zug trägt die neue Aufgabe in die Liste (#301), wie Notiz und Memo in die Pinnwand.
            onPointerDown={onAddTaskPointerDown}
            draggable={false}
            disabled={disabled}
          >
            <ListToolbarAddIcon size={24} />
            <AnimatePresence initial={false}>
              {labelTask && <Label key="label" text={t('listToolbar.addTask')} />}
            </AnimatePresence>
          </button>
        </WithTooltip>
      </div>

      <AnimatePresence initial={false} custom={sequence}>
        {showSection && (
          <Segment key="section" slot="section" sequence={sequence}>
            <div className="list-toolbar__group list-toolbar__group--section">
              <WithTooltip
                side="top"
                sideOffset={TOOLTIP_OFFSET}
                content={addSectionDragging ? undefined : addSectionTooltip}
              >
                <button
                  type="button"
                  aria-label={t('listToolbar.addSection')}
                  className={`${buttonClass(labelOthers, Boolean(onAddSectionPointerDown))}${addSectionDragging ? ' list-toolbar__button--dragging' : ''}`}
                  onClick={onAddSection}
                  // Ein Zug trägt den neuen Abschnitt an eine Stelle der Liste (#314), wie „Neue Aufgabe“.
                  onPointerDown={onAddSectionPointerDown}
                  draggable={false}
                  disabled={disabled}
                >
                  <ListToolbarSectionIcon size={24} />
                  {labelOthers && <span className="list-toolbar__label">{t('listToolbar.addSection')}</span>}
                </button>
              </WithTooltip>
            </div>
          </Segment>
        )}

        {(placementShown || showSelection) && (
          // EIN Trenner für Notiz/Memo und die Auswahl: Er kommt mit der Notiz bzw. der Auswahl und bleibt
          // stehen, wenn beide tauschen (#291).
          <Segment key="divider" slot={showSelection ? 'selection' : 'note'} sequence={sequence} className="list-toolbar__segment--divider">
            <div className="list-toolbar__divider" aria-hidden="true">
              <span className="list-toolbar__divider-line" />
            </div>
          </Segment>
        )}
      </AnimatePresence>

      {/*
        **Der Platz hinter dem Trenner** — Notiz und Memo oder die Befehle der Auswahl. Im Tausch
        (`--swapping`) liegt die Auswahl über Notiz und Memo, und der Platz fährt seine Breite in einem Zug
        von der einen zur anderen (`swapTimeline`).
      */}
      <div ref={swapRef} className={`list-toolbar__swap${swap ? ' list-toolbar__swap--swapping' : ''}`}>
        <AnimatePresence initial={false} custom={sequence}>
          {showNote && (
            <Segment key="note" slot="note" sequence={sequence}>
              <div className="list-toolbar__group list-toolbar__group--note">
                <WithTooltip side="top" sideOffset={TOOLTIP_OFFSET} content={t('listToolbar.placeNote')} shortcut={{ kind: 'character', text: 'N' }}>
                  <button
                    type="button"
                    aria-label={t('listToolbar.placeNote')}
                    data-swap-item="note"
                    className={buttonClass(labelOthers, Boolean(onNotePointerDown))}
                    onClick={onPlaceNote}
                    // Ein Zug trägt den Zettel in die Pinnwand, wie an der Werkzeug-Pille.
                    onPointerDown={onNotePointerDown}
                    // Sonst startete der Browser sein eigenes Zieh-Bild.
                    draggable={false}
                    disabled={disabled}
                  >
                    <CanvasNoteIcon size={24} />
                    {labelOthers && <span className="list-toolbar__label">{t('listToolbar.placeNote')}</span>}
                  </button>
                </WithTooltip>
              </div>
            </Segment>
          )}
          {showMemo && (
            <Segment key="memo" slot="memo" sequence={sequence}>
              <div className="list-toolbar__group list-toolbar__group--memo">
                <WithTooltip side="top" sideOffset={TOOLTIP_OFFSET} content={t('listToolbar.placeMemo')} shortcut={{ kind: 'character', text: 'M' }}>
                  <button
                    type="button"
                    aria-label={t('listToolbar.placeMemo')}
                    data-swap-item="memo"
                    className={buttonClass(labelOthers, Boolean(onMemoPointerDown))}
                    onClick={onPlaceMemo}
                    onPointerDown={onMemoPointerDown}
                    draggable={false}
                    disabled={disabled}
                  >
                    <CanvasMemoIcon size={24} />
                    {labelOthers && <span className="list-toolbar__label">{t('listToolbar.placeMemo')}</span>}
                  </button>
                </WithTooltip>
              </div>
            </Segment>
          )}
          {showSelection && (
            <Segment key="selection" slot="selection" sequence={sequence} className="list-toolbar__segment--selection">
              {selection}
            </Segment>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
})
